type CachedImageStatus = 'loading' | 'loaded' | 'error';

type CachedImageEntry = {
  status: CachedImageStatus;
  promise?: Promise<void>;
};

const imageCache = new Map<string, CachedImageEntry>();

const canUseImageApi = () => typeof window !== 'undefined' && typeof Image !== 'undefined';

export const getCachedImageStatus = (src: string): CachedImageStatus | null => {
  if (!src) {
    return null;
  }

  return imageCache.get(src)?.status ?? null;
};

export const isImageCached = (src: string) => getCachedImageStatus(src) === 'loaded';

export const markImageCached = (src: string) => {
  if (!src) {
    return;
  }

  imageCache.set(src, { status: 'loaded' });
};

export const markImageErrored = (src: string) => {
  if (!src) {
    return;
  }

  imageCache.set(src, { status: 'error' });
};

export const cacheImage = (src: string): Promise<void> => {
  if (!src) {
    return Promise.resolve();
  }

  const existingEntry = imageCache.get(src);
  if (existingEntry?.status === 'loaded') {
    return Promise.resolve();
  }

  if (existingEntry?.status === 'loading' && existingEntry.promise) {
    return existingEntry.promise;
  }

  if (!canUseImageApi()) {
    markImageCached(src);
    return Promise.resolve();
  }

  const loadPromise = new Promise<void>((resolve, reject) => {
    const image = new Image();
    image.onload = () => {
      markImageCached(src);
      resolve();
    };
    image.onerror = () => {
      markImageErrored(src);
      reject(new Error(`Failed to preload image: ${src}`));
    };
    image.src = src;
  });

  imageCache.set(src, { status: 'loading', promise: loadPromise });

  return loadPromise;
};
